import { useEffect, useState, useRef, useCallback } from 'react';

// Check if IntersectionObserver is available
const hasIntersectionObserver = typeof window !== 'undefined' && !!window.IntersectionObserver;

export const useIntersectionObserver = (threshold = 0) => {
    // Use lazy initial state to set initial values based on IntersectionObserver availability
    const [isIntersecting, setIsIntersecting] = useState(() => !hasIntersectionObserver);
    const [hasRendered, setHasRendered] = useState(() => !hasIntersectionObserver);
    const [count, setCount] = useState(0);
    const ref = useRef<HTMLDivElement>(null);

    const handleIntersection = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            if (entries && entries.length > 0) {
                const entry = entries[0];
                setIsIntersecting(entry.isIntersecting);
                if (entry.isIntersecting && !hasRendered) {
                    if (count > 1) setHasRendered(true);
                    else setCount((prevCount) => prevCount + 1);
                }
            }
        },
        [hasRendered, count]
    );

    useEffect(() => {
        // Make sure we're in the browser environment with IntersectionObserver
        if (!hasIntersectionObserver) {
            return;
        }

        const observer = new IntersectionObserver(handleIntersection, {
            threshold,
            root: null,
            rootMargin: '0px',
        });

        const element = ref.current;
        if (element) {
            observer.observe(element);
        }

        return () => {
            if (element && observer) {
                observer.unobserve(element);
                observer.disconnect();
            }
        };
    }, [threshold, handleIntersection]);

    // For SSR, assume true initially
    const isVisible = typeof window === 'undefined' ? true : isIntersecting || hasRendered;

    // Use type assertion to make TypeScript happy
    return [isVisible, ref] as [boolean, React.RefObject<HTMLDivElement>];
};

export default useIntersectionObserver;
